import type {
  SystemStyleObject,
  TabProps as SystemTabProps,
  TabListProps,
  TabPanelProps,
  TabPanelsProps,
  TabsProps,
} from '@hover/blueprint';
import {
  Box,
  Center,
  Tab as SystemTab,
  TabList,
  TabPanels,
  Tabs,
  Toggle,
  calc,
  createContext,
  theme as defaultTheme,
  forwardRef,
  motion,
  system,
  transition,
  useLatestCallback,
  useTheme,
} from '@hover/blueprint';
import type { MultiStyleConfig } from '@hover/blueprint/chakra';
import {
  useTabPanel,
  useTabsContext,
  useTabsStyles,
} from '@hover/blueprint/chakra';
import { includes } from '@hover/utilities';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback } from 'react';

import { useImperativeTracking } from 'src/modules/homeownerApp/hooks/useImperativeTracking';
import { AnimatedTextWidth } from 'src/shared/components/AnimatedTextWidth';
import { useCachedAiGeneratedImageRegions } from 'src/shared/graphql/cache';
import { fadePresence } from 'src/shared/utils/motion';

import {
  type PaintRegion,
  type Region,
  paintRegions,
} from '../../constants/customizationCategories';

import { getEnabledRegions } from './menu';
import {
  useCustomization,
  useCustomizationSegmentation,
} from './useCustomization';

const [Provider, useContext] = createContext<SubRegionsContext>({
  name: 'SubRegionsContext',
});

/**
 * Sub-region identifier. This is what gets sent to the backend as the `region`
 * variable when applying customizations.
 *
 * @remarks We currently only support sub-regions for paint. If and when we add
 * support for material sub-regions, we'll want to incorporate them into this
 * type.
 */
export type SubRegion = PaintRegion;

const segmentedTabListStyles = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 200,
} as const satisfies SystemStyleObject;

export const buttonStyles = {
  bg: 'neutral.800',
  border: 'none',
  color: 'lightAlpha.600',
  borderRadius: 'round',
  cursor: 'pointer',
  appearance: 'none',
  fontFamily: 'body',
  fontWeight: 'bodyBold',
  textStyle: 'body500',
  outline: '2px solid transparent !important',
  outlineOffset: '2px',
  _active: { bg: 'neutral.700' },
  _focusVisible: { outlineColor: 'neutral.0 !important' },
  _hover: { color: 'lightAlpha.700', bg: 'neutral.700' },
  ...transition.easing250('background-color', 'color', 'outline'),
} as const;

/**
 * @note The styles on this button should match `SelectPaletteButton`
 *
 * @todo(design-system): Migrate this to a design system `Button` variant once
 * the system button supports this style, or at the very least extract the
 * styles shared with the `SelectPaletteButton`
 *
 * @see {@link ./SelectPaletteButton}
 */
const segmentedStyleConfig: MultiStyleConfig = {
  parts: defaultTheme.components.Tabs.parts as string[],
  baseStyle: () => ({
    tab: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      px: 500,
      py: 0,
      height: 400,
      _selected: {
        color: 'neutral.0',
        cursor: 'default',
        _hover: { color: 'neutral.0', bg: 'neutral.800' },
      },
      ...buttonStyles,
    },
    tablist: segmentedTabListStyles,
  }),
};

interface SegmentedSubRegionTabProps extends Omit<TabProps, 'layoutId'> {
  layoutId?: never;
}

const MotionCenter = motion(Center);

const SegmentedSubRegionTab = forwardRef<SegmentedSubRegionTabProps, 'button'>(
  (
    {
      isSelected,
      label: { region: regionLabel, toggle: toggleLabel },
      onSelectAllClick,
      ...props
    },
    ref,
  ) => {
    const { areAllSelected, toggleSelectAll, hasSegmentation, selectionMode } =
      useCustomizationSegmentation();

    const { sizes, space } = useTheme();

    const showToggle =
      isSelected && hasSegmentation && selectionMode === 'instance';
    const label = showToggle ? toggleLabel : regionLabel;

    const toggleProps = showToggle
      ? ({
          'aria-label': toggleLabel,
          'aria-checked': areAllSelected,
          _hover: { bg: 'neutral.700 !important' },
          cursor: 'pointer !important',
          // The current design calls for the subregion tab to also control the
          // select all/none toggle, so we expose the checked state on the tab
          // as `aria-checked` and interpret click events as toggles. If this
          // turns out to be terrible for accessibility, we may want to remove
          // this and the `tabindex={-1}` on the `Toggle` below
          onClickCapture: event => {
            event.stopPropagation();
            onSelectAllClick?.(!areAllSelected);
            toggleSelectAll();
          },
        } satisfies SystemTabProps)
      : {};

    return (
      <SystemTab {...props} {...toggleProps} ref={ref}>
        <AnimatedTextWidth contentKey={label}>{label}</AnimatedTextWidth>
        <MotionCenter
          animate={{
            width: showToggle ? calc.add(sizes[350], space[200]) : '0px',
          }}
          position="relative"
        >
          <AnimatePresence initial={false}>
            {showToggle && (
              <MotionCenter {...fadePresence}>
                <Toggle
                  isChecked={areAllSelected}
                  mr="-100"
                  onChange={toggleSelectAll}
                  pointerEvents="none"
                  position="absolute"
                  right={0}
                  size="small"
                  sx={{
                    // TODO(design-system): remove this once the
                    // `Toggle` theme has been updated for Bauhaus
                    '& .chakra-switch__track': {
                      borderColor: 'neutral.400',
                      bg: 'transparent',
                      _checked: {
                        borderColor: 'neutral.400',
                        bg: 'neutral.400',
                      },
                    },
                  }}
                  tabIndex={-1}
                />
              </MotionCenter>
            )}
          </AnimatePresence>
        </MotionCenter>
      </SystemTab>
    );
  },
);

interface TabProps extends Omit<SystemTabProps, 'children'> {
  children?: never;
  isSelected: boolean;
  label: SubRegionLabel;
  onSelectAllClick?: (allSelected: boolean) => void;
}

const Tab = forwardRef<TabProps, 'button'>(
  ({ onSelectAllClick, ...props }, ref) => {
    // It seems that Chakra uses refs to manage the selected state of tabs,
    // which causes the selection state to flash unselected on server render
    const visuallySelected = props.isSelected
      ? { 'data-selected': 'true' as const }
      : {};

    const tabProps = { ...visuallySelected, ...props, ref };
    return (
      <SegmentedSubRegionTab
        onSelectAllClick={onSelectAllClick}
        {...tabProps}
      />
    );
  },
);

export interface SubRegionsTabsProps extends Omit<TabListProps, 'children'> {}

export const SubRegionsTabs = forwardRef<SubRegionsTabsProps, 'div'>(
  (props, ref) => {
    const { targetImage: image, category } = useCustomization();
    const { selectionMode } = useCustomizationSegmentation();

    const { selectedIndex } = useTabsContext();
    const { subRegions, getSubRegionLabel } = useContext();

    const { trackingRef } = useImperativeTracking();

    const imageRegions = useCachedAiGeneratedImageRegions(
      image.aiGeneratedImage.id,
    ).data;
    const regions = getEnabledRegions(subRegions, imageRegions);
    const selectedRegion = regions[selectedIndex];

    const isMaterialCategory = category?.type === 'material';
    const Component = isMaterialCategory ? Box : TabList;

    const trackSelectAllClick = useCallback(
      (allSelected: boolean) => {
        trackingRef.current?.buttonPressed({
          button_text: allSelected ? 'toggle on' : 'toggle off',
          primary_cta: false,
          track_id: 'all-sub-regions-toggle',
        });
      },
      [trackingRef],
    );

    return (
      <Component
        {...props}
        {...(isMaterialCategory ? segmentedTabListStyles : {})}
        ref={ref}
      >
        <AnimatePresence initial={false}>
          {isMaterialCategory && selectionMode === 'instance' && (
            <SegmentedSubRegionTab
              as={motion.button}
              isSelected
              label={getSubRegionLabel(category.region)}
              onSelectAllClick={trackSelectAllClick}
            />
          )}
        </AnimatePresence>

        {regions.map(region => (
          <Tab
            isSelected={region === selectedRegion}
            key={region}
            label={getSubRegionLabel(region)}
            onClick={() => {
              trackingRef.current?.buttonPressed({
                button_text: region,
                primary_cta: false,
                track_id: 'edit-region',
              });
            }}
            onSelectAllClick={trackSelectAllClick}
          />
        ))}
      </Component>
    );
  },
);

export interface SubRegionLabel {
  region: string;
  toggle: string;
}

interface SubRegionsContext {
  subRegions: readonly SubRegion[];
  getSubRegionLabel: (region: Region) => SubRegionLabel;
}

export const useSubRegions = useContext;

export const usePaintSubRegions = () => {
  const { subRegions, ...rest } = useContext();

  return {
    subRegions: subRegions.filter(region => includes(paintRegions, region)),
    ...rest,
  };
};

export interface SubRegionsProviderProps
  extends Omit<TabsProps, 'onChange'>,
    SubRegionsContext {
  selectedSubRegion: SubRegion;
  onSelectSubRegion: (region: SubRegion) => void;
}

export const SubRegionsProvider = ({
  children,
  subRegions,
  getSubRegionLabel,
  selectedSubRegion,
  onSelectSubRegion,
  ...props
}: SubRegionsProviderProps) => {
  const { category } = useCustomization();

  return (
    <Provider
      value={{
        subRegions,
        getSubRegionLabel: useLatestCallback(getSubRegionLabel, []),
      }}
    >
      <Tabs
        index={subRegions.indexOf(selectedSubRegion)}
        // NOTE: the `<AnimatePresence>` transition implementation in
        // `SubRegionPanel` below forces `isLazy` + `lazyBehavior="unmount"`
        // behavior. If we need to update the lazy behavior for these tab panels,
        // we'll need to update that as well to keep panels mounted.
        isLazy
        lazyBehavior="unmount"
        onChange={index =>
          category?.type === 'paint' && onSelectSubRegion(subRegions[index])
        }
        styleConfig={segmentedStyleConfig}
        {...props}
      >
        {children}
      </Tabs>
    </Provider>
  );
};

export type SubRegionsPanelsProps = TabPanelsProps;

export const SubRegionsPanels = forwardRef<SubRegionsPanelsProps, 'div'>(
  (props, ref) => <TabPanels position="relative" {...props} ref={ref} />,
);

export type SubRegionPanelProps = TabPanelProps;

export const SubRegionPanel = forwardRef<SubRegionPanelProps, 'div'>(
  ({ children, sx, ...props }, ref) => {
    const panelProps = useTabPanel({ ...props, ref });
    const styles = useTabsStyles();

    const { hidden } = panelProps;

    return (
      <system.div
        outline="0"
        sx={{
          ...sx,
          zIndex: 1,
          '&[hidden]': {
            display: 'block !important',
            zIndex: -1,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
          },
        }}
        {...panelProps}
        __css={styles.tabpanel}
        ref={ref}
      >
        <AnimatePresence initial={false} mode="popLayout">
          {!hidden && (
            <motion.div
              animate={{ opacity: 1 }}
              exit={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </system.div>
    );
  },
);
